/**
 * `next/navigation`, on react-router (cascade plan task 2.7).
 *
 * The ds/ components are copied byte-for-byte from d2d-XDR (siem-frontend), a
 * Next.js app. Exactly one of them, Tabs (URL-synced tabs), imports from
 * `next/navigation`. vite.config.ts, vitest.config.ts and tsconfig.json alias
 * that specifier here, so the copy stays identical to SIEM's.
 *
 * Covers only what Tabs uses: usePathname, useSearchParams (read-only) and
 * useRouter().replace/push. Anything else Next exports is deliberately absent,
 * so a new use fails at build time instead of misbehaving at run time.
 */
import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

export function usePathname(): string {
  return useLocation().pathname
}

/** Next returns a read-only URLSearchParams; so does this. */
export function useSearchParams(): Omit<URLSearchParams, 'append' | 'delete' | 'set' | 'sort'> {
  const { search } = useLocation()
  return useMemo(() => new URLSearchParams(search), [search])
}

interface NavigateOptions {
  /** Next's scroll-to-top flag. react-router never scrolls on navigate, so this is a no-op. */
  scroll?: boolean
}

export function useRouter() {
  const navigate = useNavigate()
  const replace = useCallback((href: string, _opts?: NavigateOptions) => navigate(href, { replace: true }), [navigate])
  const push = useCallback((href: string, _opts?: NavigateOptions) => navigate(href), [navigate])
  return useMemo(() => ({ replace, push, back: () => navigate(-1) }), [replace, push, navigate])
}
